import { FC } from "react";

import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import styled from "styled-components";
import formatPropertyName from "utils/column/formatPropertyName";
import getColumnLabelWithContext from "utils/column/getColumnLabelWithContext";
import getGroupByListColumnByProperty from "utils/column/getGroupByListColumnByProperty";

import { useEvaUnitCore } from "../../hooks";
import { IGroupByListItem } from "../../models";
import { MlFeature, NormalizeFeaturePackage } from "./types";
import { resolveNormalizationUnit } from "./utils/nameUtils";

interface TemplateFeaturesTableProps {
  features: MlFeature[];
  normalizationFeatures?: NormalizeFeaturePackage[];
  unitType?: number;
  categoryList: IGroupByListItem[];
}

interface TableFeature {
  key: number;
  name: string;
  monotonic: number;
  normalizedBy: string;
}

/**
 * Displays a read-only table of features for template preview in the load modal.
 * Shows feature names (with units), monotonicity arrows, and an optional
 * "Normalized By" column when normalization settings are present.
 */
const TemplateFeaturesTable: FC<TemplateFeaturesTableProps> = ({
  features,
  normalizationFeatures = [],
  unitType = 0,
  categoryList
}) => {
  const { isReady: isUnitCoreReady, getTargetUnit } = useEvaUnitCore();
  const hasNormalization = normalizationFeatures.length > 0;

  // Convert unit in title based on unitType
  const convertTitleUnit = (title: string): string => {
    if (!isUnitCoreReady) return title;

    const unitMatch = title.match(/\(([^)]+)\)/);
    if (!unitMatch) return title;

    try {
      const sourceUnit = unitMatch[1];
      const targetUnit = getTargetUnit(sourceUnit, unitType);
      if (targetUnit && targetUnit !== sourceUnit) {
        return title.replace(`(${sourceUnit})`, `(${targetUnit})`);
      }
    } catch (error) {
      // getTargetUnit can fail for some unit conversions
    }

    return title;
  };

  const columns: ColumnsType<TableFeature> = [
    {
      title: "Feature Name",
      dataIndex: "name",
      key: "name",
      width: hasNormalization ? "45%" : "70%"
    },
    {
      title: "Monotonicity",
      dataIndex: "monotonic",
      key: "monotonic",
      width: hasNormalization ? "15%" : "30%",
      align: "center",
      render: (value: number) => {
        if (value === 1) {
          return (
            <span style={{ color: "#52c41a", fontWeight: "bold", fontSize: "16px" }}>
              ↑
            </span>
          );
        }
        if (value === -1) {
          return (
            <span style={{ color: "#f5222d", fontWeight: "bold", fontSize: "16px" }}>
              ↓
            </span>
          );
        }
        return (
          <span style={{ color: "#bfbfbf", fontWeight: "bold", fontSize: "16px" }}>
            -
          </span>
        );
      }
    },
    ...(hasNormalization
      ? [
          {
            title: "Normalized By",
            dataIndex: "normalizedBy",
            key: "normalizedBy",
            width: "40%",
            render: (value: string) => (
              <span style={{ color: value ? undefined : "#bfbfbf" }}>{value || "-"}</span>
            )
          }
        ]
      : [])
  ];

  const normalizationMap = new Map(
    normalizationFeatures.map((n) => [n.ApplyTo.Property, n.NormalizeBy])
  );

  const dataSource: TableFeature[] = features.map((feature, idx) => {
    // Use getColumnLabelWithContext for consistent formatting
    const col = getGroupByListColumnByProperty(
      categoryList,
      formatPropertyName(feature.Property)
    );
    const contextTitle = col ? getColumnLabelWithContext(col, false, undefined) : null;
    let featureDisplayName = contextTitle?.fullContextTitle || feature.NiceName;

    // Convert unit in display name based on unitType
    featureDisplayName = convertTitleUnit(featureDisplayName);

    const normalizeBy = normalizationMap.get(feature.Property);
    let normalizedByLabel = "";
    if (normalizeBy) {
      // Format the normalizeBy feature name
      const normCol = getGroupByListColumnByProperty(
        categoryList,
        formatPropertyName(normalizeBy.Property)
      );
      const normContextTitle = normCol
        ? getColumnLabelWithContext(normCol, false, undefined)
        : null;
      const normName = normContextTitle?.fullContextTitle || normalizeBy.NiceName;

      // Build value and unit label
      const valueParts = [];
      if (normalizeBy.Value != null) {
        valueParts.push(`${normalizeBy.Value}`);
      }
      const unit = resolveNormalizationUnit(normalizeBy, unitType, categoryList);
      if (unit) {
        valueParts.push(unit);
      }
      normalizedByLabel = valueParts.length
        ? `${normName} (${valueParts.join(" ")})`
        : normName;
    }

    return {
      key: idx,
      name: featureDisplayName,
      monotonic: feature.Monotonic,
      normalizedBy: normalizedByLabel
    };
  });

  return (
    <TableWrapper>
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        size="small"
        bordered
      />
    </TableWrapper>
  );
};

export default TemplateFeaturesTable;

const TableWrapper = styled.div`
  .ant-table-tbody > tr.ant-table-row:hover > td,
  .ant-table-tbody > tr > td.ant-table-cell-row-hover {
    background: inherit;
  }
`;
